import React, { useEffect, useState } from 'react';
import { useAwsImageUrl } from '../../hooks/useAwsImageUrl';

const skeletonCss = `
@keyframes adminAwsImagePulse {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.admin-aws-image--loading {
  background-image: linear-gradient(90deg, #eef2f7 0%, #f8fafc 45%, #e5eaf2 100%);
  background-size: 220% 100%;
  animation: adminAwsImagePulse 1.15s ease-in-out infinite;
}
`;

const AwsImageSkeletonStyle = () => <style data-admin-aws-image-skeleton>{skeletonCss}</style>;

const AwsImage = ({
    awsId,
    aws,
    fallbackUrl,
    variant,
    usage,
    width,
    height,
    loading = 'lazy',
    decoding = 'async',
    alt = '',
    skeleton = true,
    fallback = null,
    className,
    style,
    onLoad,
    onError,
    ...props
}) => {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [renderAttempt, setRenderAttempt] = useState(0);
    const [retryAttempted, setRetryAttempted] = useState(false);
    const numericWidth = typeof width === 'number' ? width : undefined;
    const numericHeight = typeof height === 'number' ? height : undefined;
    const image = useAwsImageUrl({
        awsId,
        aws,
        fallbackUrl,
        variant,
        usage,
        width: numericWidth,
        height: numericHeight
    });
    const isLoading = skeleton && (image.isLoading || retrying || (Boolean(image.url) && !loaded && !failed));
    const combinedClassName = [className, isLoading ? 'admin-aws-image--loading' : undefined]
        .filter(Boolean)
        .join(' ') || undefined;

    useEffect(() => {
        setLoaded(false);
        setFailed(false);
        setRetrying(false);
    }, [image.url]);

    useEffect(() => {
        setLoaded(false);
        setFailed(false);
        setRetrying(false);
        setRenderAttempt(0);
        setRetryAttempted(false);
    }, [awsId, aws?.id, fallbackUrl, usage, variant]);

    const fallbackContent = typeof fallback === 'function' ? fallback() : fallback;

    if (failed) {
        return fallbackContent;
    }

    if (retrying) {
        return (
            <>
                <AwsImageSkeletonStyle />
                <span
                    className="admin-aws-image--loading"
                    data-loading="true"
                    style={{ display: 'block', width, height, ...style }}
                />
            </>
        );
    }

    if (!image.url) {
        if (!isLoading) return fallbackContent;

        return (
            <>
                <AwsImageSkeletonStyle />
                <span
                    className={combinedClassName}
                    data-loading="true"
                    style={{
                        display: 'block',
                        width,
                        height,
                        ...style
                    }}
                />
            </>
        );
    }

    return (
        <>
            {skeleton && <AwsImageSkeletonStyle />}
            <img
                key={`${image.url}-${renderAttempt}`}
                {...props}
                src={image.url}
                width={width}
                height={height}
                alt={alt}
                loading={loading}
                decoding={decoding}
                className={combinedClassName}
                data-loading={isLoading ? 'true' : 'false'}
                style={{
                    ...style,
                    opacity: isLoading ? 0.35 : style?.opacity,
                    transition: 'opacity 160ms ease'
                }}
                onLoad={(event) => {
                    setLoaded(true);
                    onLoad?.(event);
                }}
                onError={(event) => {
                    onError?.(event);
                    if (!retryAttempted && image.canRefresh) {
                        setRetryAttempted(true);
                        setRetrying(true);
                        image.refresh()
                            .catch(() => undefined)
                            .finally(() => {
                                setLoaded(false);
                                setRetrying(false);
                                setRenderAttempt((current) => current + 1);
                            });
                        return;
                    }
                    setLoaded(true);
                    setFailed(true);
                }}
            />
        </>
    );
};

export default AwsImage;
